import React, { useState, useEffect, useMemo } from 'react';
import logo from '../logo_blade.png';
import { auth, db } from '../services/firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { collection, getDocs } from 'firebase/firestore';

const ADMIN_UID = 'psJJtLPO6cVnu5Sq529xY1t1SQD3';
const MESES_PT = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const MESES_CURTO = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];

function parsePreco(preco) {
  if (!preco || typeof preco !== 'string') return 0;
  const num = preco.replace('R$', '').replace(/\s/g, '').replace(/\./g, '').replace(',', '.');
  const val = parseFloat(num);
  return isNaN(val) ? 0 : val;
}

function formatarBRL(valor) {
  return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function formatarData(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString('pt-BR'); } catch { return '—'; }
}

function hoje() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
}

const anoAtual = new Date().getFullYear();
const ANOS = Array.from({ length: anoAtual - 2023 }, (_, i) => 2024 + i);

// ── Gráfico de barras SVG ──────────────────────────────────────────────────
function GraficoBarras({ dados }) {
  const maxVal = Math.max(...dados.map(d => d.total), 1);
  const alturaMax = 100;
  const larguraBarra = dados.length > 15 ? 22 : dados.length > 8 ? 30 : 40;
  const gap = dados.length > 15 ? 6 : 12;
  const totalWidth = dados.length * (larguraBarra + gap) - gap;

  return (
    <div style={{ overflowX: 'auto' }}>
      <svg width={totalWidth + 40} height={alturaMax + 48} style={{ display: 'block', margin: '0 auto' }}>
        {dados.map((d, i) => {
          const altura = Math.max(maxVal === 0 ? 0 : Math.round((d.total / maxVal) * alturaMax), d.total > 0 ? 4 : 0);
          const x = i * (larguraBarra + gap) + 20;
          const y = alturaMax - altura;
          return (
            <g key={i}>
              <rect x={x} y={y} width={larguraBarra} height={altura} fill="#2d4a2d" rx="3" />
              {d.total > 0 && (
                <text x={x + larguraBarra / 2} y={y - 4} textAnchor="middle"
                  fill="#7bc47b" fontSize="10" fontFamily="Arial, sans-serif">{d.total}</text>
              )}
              <text x={x + larguraBarra / 2} y={alturaMax + 16} textAnchor="middle"
                fill="#666" fontSize={dados.length > 15 ? '9' : '11'} fontFamily="Arial, sans-serif">{d.label}</text>
            </g>
          );
        })}
        <line x1={20} y1={alturaMax} x2={totalWidth + 20} y2={alturaMax} stroke="#2a2a2a" strokeWidth="1" />
      </svg>
    </div>
  );
}

// ── KPI Card ──────────────────────────────────────────────────────────────
function KpiCard({ label, valor, sub, destaque }) {
  return (
    <div style={{
      backgroundColor: '#181818', border: `0.5px solid ${destaque ? '#3d6b3d' : '#2a2a2a'}`,
      borderRadius: '12px', padding: '16px 18px', flex: '1', minWidth: '130px',
    }}>
      <div style={{ color: '#9c9c9c', fontSize: '11px', fontFamily: 'Arial, sans-serif', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>{label}</div>
      <div style={{ color: destaque ? '#7bc47b' : '#e8e8e8', fontSize: '20px', fontWeight: '700', fontFamily: 'Arial, sans-serif' }}>{valor}</div>
      {sub && <div style={{ color: '#8f8f8f', fontSize: '11px', fontFamily: 'Arial, sans-serif', marginTop: '3px' }}>{sub}</div>}
    </div>
  );
}

// ── Select estilizado ─────────────────────────────────────────────────────
function Sel({ value, onChange, children, style }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)} style={{
      backgroundColor: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: '8px',
      color: '#e8e8e8', padding: '8px 12px', fontSize: '12px',
      fontFamily: 'Arial, sans-serif', outline: 'none', cursor: 'pointer', ...style,
    }}>{children}</select>
  );
}

// ── Login Screen ──────────────────────────────────────────────────────────
function TelaLogin({ onLogin }) {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState('');
  const [carregando, setCarregando] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setErro('');
    setCarregando(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email.trim(), senha);
      if (cred.user.uid !== ADMIN_UID) {
        await auth.signOut();
        setErro('Acesso não autorizado.');
      }
    } catch {
      setErro('Email ou senha incorretos.');
    }
    setCarregando(false);
  }

  const inputStyle = {
    width: '100%', boxSizing: 'border-box',
    backgroundColor: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: '10px',
    color: '#e8e8e8', padding: '12px 14px', fontSize: '14px',
    fontFamily: 'Arial, sans-serif', outline: 'none',
  };

  return (
    <div style={{ backgroundColor: '#111111', minHeight: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
      <img src={logo} alt="BLADE" style={{ height: '36px', objectFit: 'contain', marginBottom: '32px' }} />
      <div style={{ backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '16px', padding: '32px 28px', width: '100%', maxWidth: '320px' }}>
        <div style={{ color: '#e8e8e8', fontSize: '15px', fontWeight: '600', fontFamily: 'Arial, sans-serif', marginBottom: '4px' }}>Acesso restrito</div>
        <div style={{ color: '#8f8f8f', fontSize: '12px', fontFamily: 'Arial, sans-serif', marginBottom: '24px' }}>Painel administrativo BLADE</div>
        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: '12px' }}>
            <input type="email" placeholder="Email" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <input type="password" placeholder="Senha" value={senha} onChange={e => setSenha(e.target.value)} required style={inputStyle} />
          </div>
          {erro && <div style={{ color: '#e24b4a', fontSize: '12px', fontFamily: 'Arial, sans-serif', marginBottom: '14px', textAlign: 'center' }}>{erro}</div>}
          <button type="submit" disabled={carregando} style={{
            width: '100%', backgroundColor: '#2d4a2d', color: '#7bc47b', border: 'none',
            borderRadius: '12px', padding: '13px', fontSize: '14px', fontWeight: '600',
            fontFamily: 'Arial, sans-serif', cursor: carregando ? 'not-allowed' : 'pointer', opacity: carregando ? 0.7 : 1,
          }}>{carregando ? 'Entrando...' : 'Entrar'}</button>
        </form>
      </div>
    </div>
  );
}

// ── Dashboard principal ───────────────────────────────────────────────────
export default function AdminDashboard() {
  const [authChecked, setAuthChecked] = useState(false);
  const [autorizado, setAutorizado] = useState(false);
  const [carregando, setCarregando] = useState(true);

  const [barbearias, setBarbearias] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [agendamentos, setAgendamentos] = useState([]);

  // Filtros de período
  const [tipoPeriodo, setTipoPeriodo] = useState('mes');
  const [filtroAno, setFiltroAno] = useState(anoAtual);
  const [filtroMes, setFiltroMes] = useState(new Date().getMonth());
  const [filtroDia, setFiltroDia] = useState(hoje());

  // Filtros geográficos
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroCidade, setFiltroCidade] = useState('');

  // Abas
  const [abaSelecionada, setAbaSelecionada] = useState('barbearias');
  const [buscaBarbearia, setBuscaBarbearia] = useState('');
  const [buscaUsuario, setBuscaUsuario] = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged((user) => {
      setAutorizado(!!(user && user.uid === ADMIN_UID));
      setAuthChecked(true);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!autorizado) return;
    async function carregar() {
      setCarregando(true);
      try {
        const [barbSnap, userSnap, agendSnap] = await Promise.all([
          getDocs(collection(db, 'barbearias')),
          getDocs(collection(db, 'clientes')),
          getDocs(collection(db, 'agendamentos')),
        ]);
        setBarbearias(barbSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setUsuarios(userSnap.docs.map(d => ({ id: d.id, ...d.data() })));
        setAgendamentos(agendSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (e) {
        console.error('Erro ao carregar dados admin:', e);
      }
      setCarregando(false);
    }
    carregar();
  }, [autorizado]);

  // Estados disponíveis
  const estadosDisponiveis = useMemo(() =>
    [...new Set(barbearias.map(b => b.endereco?.estado).filter(Boolean))].sort(),
    [barbearias]);

  // Cidades disponíveis (filtradas por estado)
  const cidadesDisponiveis = useMemo(() => {
    const base = filtroEstado ? barbearias.filter(b => b.endereco?.estado === filtroEstado) : barbearias;
    return [...new Set(base.map(b => b.endereco?.cidade).filter(Boolean))].sort();
  }, [barbearias, filtroEstado]);

  function handleEstadoChange(val) {
    setFiltroEstado(val);
    setFiltroCidade('');
  }

  // IDs das barbearias que passam no filtro geográfico
  const idsBarb = useMemo(() => {
    return new Set(barbearias
      .filter(b => (!filtroEstado || b.endereco?.estado === filtroEstado) && (!filtroCidade || b.endereco?.cidade === filtroCidade))
      .map(b => b.id));
  }, [barbearias, filtroEstado, filtroCidade]);

  // Agendamentos reais (sem bloqueados) filtrados por geo + período
  const agendFiltrados = useMemo(() => {
    return agendamentos.filter(a => {
      if (a.status === 'bloqueado') return false;
      if (!idsBarb.has(a.barbeariaId)) return false;
      if (!a.criadoEm) return tipoPeriodo === 'todos';
      const d = new Date(a.criadoEm);
      if (tipoPeriodo === 'ano') return d.getFullYear() === filtroAno;
      if (tipoPeriodo === 'mes') return d.getFullYear() === filtroAno && d.getMonth() === filtroMes;
      if (tipoPeriodo === 'dia') {
        const iso = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
        return iso === filtroDia;
      }
      return true;
    });
  }, [agendamentos, idsBarb, tipoPeriodo, filtroAno, filtroMes, filtroDia]);

  // KPIs
  const receitaBruta = useMemo(() => agendFiltrados.reduce((acc, a) => acc + parsePreco(a.preco), 0), [agendFiltrados]);
  const taxaBlade = receitaBruta * 0.1;

  // Ranking por barbearia
  const ranking = useMemo(() => {
    const map = {};
    agendFiltrados.forEach(a => {
      const id = a.barbeariaId || '';
      if (!map[id]) map[id] = { total: 0, count: 0 };
      map[id].total += parsePreco(a.preco);
      map[id].count += 1;
    });
    return Object.entries(map)
      .map(([id, { total, count }]) => {
        const barb = barbearias.find(b => b.id === id);
        return {
          id,
          nome: barb?.nome || id,
          cidade: barb?.endereco?.cidade || '—',
          estado: barb?.endereco?.estado || '—',
          count,
          total,
        };
      })
      .sort((a, b) => b.total - a.total);
  }, [agendFiltrados, barbearias]);

  // Dados do gráfico
  const dadosGrafico = useMemo(() => {
    if (tipoPeriodo === 'todos') {
      const agora = new Date();
      return Array.from({ length: 5 }, (_, i) => {
        const d = new Date(agora.getFullYear(), agora.getMonth() - (4 - i), 1);
        const total = agendFiltrados.filter(a => {
          if (!a.criadoEm) return false;
          const dt = new Date(a.criadoEm);
          return dt.getFullYear() === d.getFullYear() && dt.getMonth() === d.getMonth();
        }).length;
        return { label: MESES_CURTO[d.getMonth()], total };
      });
    }
    if (tipoPeriodo === 'ano') {
      return MESES_CURTO.map((label, mes) => ({
        label,
        total: agendFiltrados.filter(a => {
          if (!a.criadoEm) return false;
          const d = new Date(a.criadoEm);
          return d.getMonth() === mes;
        }).length,
      }));
    }
    if (tipoPeriodo === 'mes') {
      const diasNoMes = new Date(filtroAno, filtroMes + 1, 0).getDate();
      return Array.from({ length: diasNoMes }, (_, i) => {
        const dia = i + 1;
        const total = agendFiltrados.filter(a => {
          if (!a.criadoEm) return false;
          const d = new Date(a.criadoEm);
          return d.getDate() === dia;
        }).length;
        return { label: String(dia), total };
      });
    }
    return [];
  }, [tipoPeriodo, filtroAno, filtroMes, agendFiltrados]);

  // Tabelas
  const barbeariasFiltradas = useMemo(() => barbearias.filter(b => {
    const q = buscaBarbearia.toLowerCase();
    if (!q) return true;
    return (b.nome || '').toLowerCase().includes(q) ||
      (b.endereco?.cidade || '').toLowerCase().includes(q) ||
      (b.email || '').toLowerCase().includes(q);
  }), [barbearias, buscaBarbearia]);

  const usuariosFiltrados = useMemo(() => usuarios.filter(u => {
    const q = buscaUsuario.toLowerCase();
    if (!q) return true;
    return (u.nome || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.telefone || '').toLowerCase().includes(q);
  }), [usuarios, buscaUsuario]);

  // ── Render guards ──
  if (!authChecked) return (
    <div style={{ backgroundColor: '#111111', minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
      <div style={{ width: '28px', height: '28px', border: '2px solid #2d4a2d', borderTopColor: '#7bc47b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
    </div>
  );

  if (!autorizado) return <TelaLogin />;

  // ── Estilos comuns ──
  const thS = { color: '#8f8f8f', fontSize: '11px', fontFamily: 'Arial, sans-serif', padding: '10px 14px', textAlign: 'left', fontWeight: '600', borderBottom: '0.5px solid #222', textTransform: 'uppercase', letterSpacing: '0.4px' };
  const tdS = { color: '#e8e8e8', fontSize: '13px', fontFamily: 'Arial, sans-serif', padding: '11px 14px', borderBottom: '0.5px solid #1a1a1a' };
  const inputStyle = { backgroundColor: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: '10px', color: '#e8e8e8', padding: '10px 14px', fontSize: '13px', fontFamily: 'Arial, sans-serif', outline: 'none', width: '100%', boxSizing: 'border-box' };

  const labelPeriodo = tipoPeriodo === 'dia' ? filtroDia
    : tipoPeriodo === 'mes' ? `${MESES_PT[filtroMes]} ${filtroAno}`
    : tipoPeriodo === 'ano' ? String(filtroAno)
    : 'Todo o período';

  return (
    <div style={{ backgroundColor: '#111111', minHeight: '100vh', fontFamily: 'Arial, sans-serif' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} tr:hover td{background:#191919}`}</style>

      {/* Header */}
      <div style={{ backgroundColor: '#111111', borderBottom: '0.5px solid #1e1e1e', padding: '14px 24px', display: 'flex', alignItems: 'center', gap: '12px' }}>
        <img src={logo} alt="BLADE" style={{ height: '26px', objectFit: 'contain' }} />
        <div style={{ color: '#333' }}>|</div>
        <div style={{ color: '#7bc47b', fontSize: '13px', fontWeight: '600' }}>Painel Admin</div>
        <div style={{ marginLeft: 'auto' }}>
          <button onClick={() => auth.signOut().then(() => { window.location.href = '/'; })}
            style={{ backgroundColor: 'transparent', color: '#8f8f8f', border: '0.5px solid #2a2a2a', borderRadius: '8px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
            Sair
          </button>
        </div>
      </div>

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px 16px' }}>

        {carregando ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <div style={{ width: '28px', height: '28px', border: '2px solid #2d4a2d', borderTopColor: '#7bc47b', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
          </div>
        ) : (<>

          {/* ── Filtros ── */}
          <div style={{ backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '12px', padding: '16px 18px', marginBottom: '16px' }}>
            <div style={{ color: '#8f8f8f', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.4px', marginBottom: '12px' }}>Filtros</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center' }}>

              {/* Período */}
              <Sel value={tipoPeriodo} onChange={setTipoPeriodo}>
                <option value="todos">Todo o período</option>
                <option value="ano">Ano</option>
                <option value="mes">Mês</option>
                <option value="dia">Dia</option>
              </Sel>

              {(tipoPeriodo === 'ano' || tipoPeriodo === 'mes') && (
                <Sel value={filtroAno} onChange={v => setFiltroAno(Number(v))}>
                  {ANOS.map(a => <option key={a} value={a}>{a}</option>)}
                </Sel>
              )}

              {tipoPeriodo === 'mes' && (
                <Sel value={filtroMes} onChange={v => setFiltroMes(Number(v))}>
                  {MESES_PT.map((m, i) => <option key={i} value={i}>{m}</option>)}
                </Sel>
              )}

              {tipoPeriodo === 'dia' && (
                <input type="date" value={filtroDia} onChange={e => setFiltroDia(e.target.value)}
                  style={{ backgroundColor: '#1a1a1a', border: '0.5px solid #2a2a2a', borderRadius: '8px', color: '#e8e8e8', padding: '8px 12px', fontSize: '12px', fontFamily: 'Arial, sans-serif', outline: 'none' }} />
              )}

              <div style={{ width: '1px', height: '28px', backgroundColor: '#252525', margin: '0 4px' }} />

              {/* Geográfico */}
              <Sel value={filtroEstado} onChange={handleEstadoChange}>
                <option value="">Todos os estados</option>
                {estadosDisponiveis.map(e => <option key={e} value={e}>{e}</option>)}
              </Sel>

              <Sel value={filtroCidade} onChange={setFiltroCidade} style={{ opacity: cidadesDisponiveis.length === 0 ? 0.4 : 1 }}>
                <option value="">Todas as cidades</option>
                {cidadesDisponiveis.map(c => <option key={c} value={c}>{c}</option>)}
              </Sel>

              {(tipoPeriodo !== 'todos' || filtroEstado || filtroCidade) && (
                <button onClick={() => { setTipoPeriodo('todos'); setFiltroEstado(''); setFiltroCidade(''); }}
                  style={{ backgroundColor: 'transparent', color: '#8f8f8f', border: '0.5px solid #2a2a2a', borderRadius: '8px', padding: '8px 12px', fontSize: '12px', cursor: 'pointer', fontFamily: 'Arial, sans-serif' }}>
                  Limpar
                </button>
              )}
            </div>

            <div style={{ marginTop: '10px', color: '#7d7d7d', fontSize: '11px' }}>
              {labelPeriodo}{filtroEstado ? ` · ${filtroEstado}` : ''}{filtroCidade ? ` · ${filtroCidade}` : ''}
            </div>
          </div>

          {/* ── KPIs ── */}
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <KpiCard label="Barbearias" valor={barbearias.length} />
            <KpiCard label="Clientes" valor={usuarios.length} />
            <KpiCard label="Agendamentos" valor={agendFiltrados.length} sub={labelPeriodo} />
            <KpiCard label="Receita Bruta" valor={formatarBRL(receitaBruta)} sub={labelPeriodo} />
            <KpiCard label="Taxa BLADE (10%)" valor={formatarBRL(taxaBlade)} sub="sobre receita bruta" destaque />
          </div>

          {/* ── Gráfico ── */}
          {tipoPeriodo !== 'dia' && dadosGrafico.length > 0 && (
            <div style={{ backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '12px', padding: '18px 16px', marginBottom: '16px' }}>
              <div style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: '600', marginBottom: '16px' }}>
                Agendamentos
                {tipoPeriodo === 'mes' && ` — ${MESES_PT[filtroMes]} ${filtroAno}`}
                {tipoPeriodo === 'ano' && ` — ${filtroAno}`}
                {tipoPeriodo === 'todos' && ' — últimos 5 meses'}
              </div>
              <GraficoBarras dados={dadosGrafico} />
            </div>
          )}

          {/* ── Ranking ── */}
          <div style={{ backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
            <div style={{ padding: '16px 18px 0' }}>
              <div style={{ color: '#e8e8e8', fontSize: '13px', fontWeight: '600', marginBottom: '2px' }}>
                Ranking de Barbearias
              </div>
              <div style={{ color: '#7d7d7d', fontSize: '11px', marginBottom: '12px' }}>{labelPeriodo}</div>
            </div>
            {ranking.length === 0 ? (
              <div style={{ color: '#7d7d7d', fontSize: '13px', textAlign: 'center', padding: '32px', fontFamily: 'Arial, sans-serif' }}>
                Nenhum agendamento no período selecionado.
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ ...thS, width: '36px' }}>#</th>
                      <th style={thS}>Barbearia</th>
                      <th style={thS}>Cidade</th>
                      <th style={thS}>UF</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Agend.</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Receita</th>
                      <th style={{ ...thS, textAlign: 'right' }}>Taxa BLADE</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ranking.map((r, i) => (
                      <tr key={r.id}>
                        <td style={{ ...tdS, color: i === 0 ? '#c9a84c' : i === 1 ? '#888' : i === 2 ? '#7b5c3a' : '#444', fontWeight: '700' }}>
                          {i + 1}
                        </td>
                        <td style={tdS}>{r.nome}</td>
                        <td style={{ ...tdS, color: '#aeaeae' }}>{r.cidade}</td>
                        <td style={{ ...tdS, color: '#aeaeae' }}>{r.estado}</td>
                        <td style={{ ...tdS, textAlign: 'right', color: '#aeaeae' }}>{r.count}</td>
                        <td style={{ ...tdS, textAlign: 'right', color: '#7bc47b', fontWeight: '600' }}>{formatarBRL(r.total)}</td>
                        <td style={{ ...tdS, textAlign: 'right', color: '#8f8f8f' }}>{formatarBRL(r.total * 0.1)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* ── Tabelas ── */}
          <div style={{ backgroundColor: '#181818', border: '0.5px solid #2a2a2a', borderRadius: '12px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', borderBottom: '0.5px solid #222' }}>
              {[{ key: 'barbearias', label: `Barbearias (${barbearias.length})` }, { key: 'clientes', label: `Clientes (${usuarios.length})` }].map(({ key, label }) => (
                <button key={key} onClick={() => setAbaSelecionada(key)} style={{
                  flex: 1, padding: '13px', border: 'none', cursor: 'pointer',
                  fontFamily: 'Arial, sans-serif', fontSize: '13px', fontWeight: '600',
                  backgroundColor: abaSelecionada === key ? '#1f2e1f' : 'transparent',
                  color: abaSelecionada === key ? '#7bc47b' : '#555',
                  borderBottom: abaSelecionada === key ? '2px solid #7bc47b' : '2px solid transparent',
                }}>{label}</button>
              ))}
            </div>

            <div style={{ padding: '14px 16px' }}>
              <input type="text"
                placeholder={abaSelecionada === 'barbearias' ? 'Buscar barbearia, cidade ou email...' : 'Buscar nome, email ou telefone...'}
                value={abaSelecionada === 'barbearias' ? buscaBarbearia : buscaUsuario}
                onChange={e => abaSelecionada === 'barbearias' ? setBuscaBarbearia(e.target.value) : setBuscaUsuario(e.target.value)}
                style={inputStyle} />
            </div>

            {abaSelecionada === 'barbearias' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={thS}>Nome</th>
                    <th style={thS}>Email</th>
                    <th style={thS}>Cidade</th>
                    <th style={thS}>UF</th>
                    <th style={thS}>Status</th>
                  </tr></thead>
                  <tbody>
                    {barbeariasFiltradas.length === 0 ? (
                      <tr><td colSpan={5} style={{ ...tdS, color: '#7d7d7d', textAlign: 'center', padding: '32px' }}>Nenhuma barbearia encontrada.</td></tr>
                    ) : barbeariasFiltradas.map(b => (
                      <tr key={b.id}>
                        <td style={tdS}>{b.nome || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{b.email || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{b.endereco?.cidade || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{b.endereco?.estado || '—'}</td>
                        <td style={tdS}>
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600',
                            backgroundColor: b.status === 'aprovado' ? '#1a2d1a' : '#2d1a1a',
                            color: b.status === 'aprovado' ? '#7bc47b' : '#e24b4a',
                            border: `0.5px solid ${b.status === 'aprovado' ? '#3d6b3d' : '#4a2d2d'}`,
                          }}>{b.status === 'aprovado' ? 'Aprovada' : 'Pendente'}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: '10px 14px', color: '#333', fontSize: '11px' }}>{barbeariasFiltradas.length} resultado{barbeariasFiltradas.length !== 1 ? 's' : ''}</div>
              </div>
            )}

            {abaSelecionada === 'clientes' && (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr>
                    <th style={thS}>Nome</th>
                    <th style={thS}>Email</th>
                    <th style={thS}>Telefone</th>
                    <th style={thS}>Cadastro</th>
                  </tr></thead>
                  <tbody>
                    {usuariosFiltrados.length === 0 ? (
                      <tr><td colSpan={4} style={{ ...tdS, color: '#7d7d7d', textAlign: 'center', padding: '32px' }}>Nenhum cliente encontrado.</td></tr>
                    ) : usuariosFiltrados.map(u => (
                      <tr key={u.id}>
                        <td style={tdS}>{u.nome || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{u.email || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{u.telefone || '—'}</td>
                        <td style={{ ...tdS, color: '#9c9c9c' }}>{formatarData(u.criadoEm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ padding: '10px 14px', color: '#333', fontSize: '11px' }}>{usuariosFiltrados.length} resultado{usuariosFiltrados.length !== 1 ? 's' : ''}</div>
              </div>
            )}
          </div>

        </>)}
      </div>
    </div>
  );
}
